import { useMemo } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { authnClient } from '@frontend/auth';
import { getInitialParams } from '@frontend/env';
import { http } from '@frontend/fetch';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { WebsocketEventPayload, useWebSocket, useWebsocketEventPublisher } from '@frontend/websocket';

export const useWeaveWebsocketConnection = () => {
  const publisher = useWebsocketEventPublisher();
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const subscribeToOrg = getFeatureFlagValue('subscription-to-organisation');
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const { websocketApi } = getInitialParams();

  const onMessage = useMemo(
    () => (payload: WebsocketEventPayload) => {
      // As soon as we've gotten all feature teams to publish and consume all notification with the schema defined notification types,
      //     we can remove the switch statement and replace it with:
      // if (payload.method in NotificationType) {
      //   // If the method is a known notification type, emit it as an event
      //   return publisher.emit(payload.method, payload);
      // }
      // // Otherwise, log a warning and return
      // console.warn('Received an unregistered websocket event', payload);
      // return payload;

      switch (payload.method) {
        case 'FaxNotification':
        case 'FollowupNotification':
        case 'FormsNotification':
        case 'NotificationAPI':
        case 'PhoneSystemEventsLegacy':
        case 'PhoneSystemEventsV2':
        case 'VOICEMAIL':
        case 'CALL_RECORD':
        case 'PhoneCallQueueEvents':
        case 'ReviewsNotification':
        case 'RunPendoGuide':
        case 'SMSConsentResponse':
        case 'SMSNotification':
        case 'ScheduledSMSNotification':
        case 'CreateTagNotification':
        case 'DeleteTagNotification':
        case 'UpdateTagNotification':
        case 'WeaveAnnouncements':
        case 'CHAT_MESSAGE':
        case 'ORG_USER_PRESENCE':
        case 'USER_PREFERENCES_UPDATE':
        case 'CALL_RECORD_EXPORT':
        case 'AnnouncementAdd':
        case 'AnnouncementRemove':
        case 'AnnouncementUpdate':
        case NotificationType.DEVICE_CONNECTION_PRESENCE_UPDATE:
        case NotificationType.DEVICE_PRESENCE_UPDATE:
        case NotificationType.TEAM_CHAT:
        case NotificationType.CALL_QUEUES: // These are all the notification types defined in schema
        case NotificationType.CHAT_MESSAGE:
        case NotificationType.FAX:
        case NotificationType.INBOUND_BLOCKED_FAX_NOTIFICATION:
        case NotificationType.FAX_COUNT_CHANGE:
        case NotificationType.FAX_READ_STATUS_CHANGE:
        case NotificationType.FAX_TAG:
        case NotificationType.FOLLOW_UP:
        case NotificationType.FORMS:
        case NotificationType.PHONE_CALL:
        case NotificationType.PHONE_CALL_V2:
        case NotificationType.REVIEWS:
        case NotificationType.SCHEDULE_REQUEST:
        case NotificationType.SCHEDULE_WAITLIST_UPDATE:
        case NotificationType.SMS:
        case NotificationType.INBOX_THREAD_ITEM:
        case NotificationType.INBOX_THREAD_ITEM_TAG:
        case NotificationType.SMS_TAG:
        case NotificationType.SMS_TAG_V2:
        case NotificationType.ROBOCALL_TAG:
        case NotificationType.SMS_TASK:
        case NotificationType.THREAD_AI_STATE:
        case NotificationType.VOICEMAIL_TRANSCRIPTION:
        case NotificationType.PAYMENTS:
        case NotificationType.MANUAL_MESSAGE_CONSENT:
        case NotificationType.SMS_MANUAL_SCHEDULED:
        case NotificationType.VOICEMAIL:
        case NotificationType.CALL_RECORD:
        case NotificationType.DEVICE_CONNECTION:
        case NotificationType.MISSED_CALL:
        case NotificationType.CALL_RECORD_TAG:
        case NotificationType.TASK:
        case NotificationType.VOICEMAIL_TAG:
        case NotificationType.APPOINTMENT_CHANGED:
        case NotificationType.BULK_APPOINTMENT_CHANGE:
        case NotificationType.SCHEDULE_CHANGED:
        case NotificationType.VOICEMAIL_MESSAGE:
        case NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL:
        case NotificationType.INSURANCE_VERIFICATION:
        case NotificationType.INBOX_AI_RECEPTIONIST_NUMBER_UPDATE:
          return publisher.emit(payload.method, payload);
        default: {
          //ensure all cases are handled. If we miss one, this ':never' here will trigger a ts error
          console.warn('Received an unregistered websocket event', payload);
          const _exhaustive: never = payload;
          return _exhaustive;
        }
      }
    },
    [publisher]
  );

  return useWebSocket({
    onMessage,
    selectedLocationIds,
    connectedDeviceSipId: phoneConfig?.sipProfileId,
    refreshAuthToken: async () => {
      const refreshResponse = await authnClient.refreshWeaveToken();
      http.setAuthorizationHeader(refreshResponse.token, refreshResponse.jtiToken);
      return refreshResponse.token as string; // refreshWeaveToken returns a token or an error and should always be defined
    },
    websocketApi,
    selectedOrgId,
    subscribeToOrg,
  });
};
